import { createSlice } from '@reduxjs/toolkit';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { apiCallBegan } from 'store/middleware/api/api';
import { depthInequality, valueTypes, kpiFractionTypes } from 'src/enums/settings';
import i18n from 'i18next';
import translations from 'src/internationalisation/translation-map.json';
import { nextName } from 'client/common/utils/next-name';
import { toast } from '@oliasoft-open-source/react-ui-library';

export const initialKpi = {
  name: '',
  valueType: valueTypes.CALCULATED,
  numerator: kpiFractionTypes.COST,
  numeratorUnit: 'USD',
  denominator: kpiFractionTypes.TIME,
  denominatorUnit: 'h',
  isTarget: true,
  targetInequality: depthInequality.LESS_OR_EQUAL,
  target: 0,
};

const userSettings = createSlice({
  name: 'userSettings',
  initialState: {
    isAdding: false,
    isFetching: false,
    showKpiForm: false,
    showDeleteKpiModal: false,
    selectedId: null,
    settings: {
      kpi: [],
    },
    currencies: [],
    error: null,
  },
  reducers: {
    userSettingsRequested: (userSettings) => {
      userSettings.isFetching = true;
      userSettings.settings = {};
    },
    userSettingsReceived: (userSettings, action) => {
      userSettings.isFetching = false;
      userSettings.settings = action.payload;
    },
    userSettingsRequestFailed: (userSettings) => {
      userSettings.isFetching = false;
    },
    userSettingsSaveRequested: (userSettings) => {
      userSettings.isAdding = true;
    },

    userSettingsSaveFailed: (userSettings) => {
      userSettings.isAdding = false;
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.settings_errorAddingTemplate),
        },
      });
    },
    userSettingsSaved: (userSettings, action) => {
      userSettings.isAdding = false;
      userSettings.settings = action.payload;
    },

    codesRequested: (userSettings) => {
      userSettings.codes = [];
    },
    codesReceived: (userSettings, action) => {
      userSettings.codes = action.payload.codes;
    },
    currenciesRequested: (userSettings) => {
      userSettings.currencies = [];
    },
    currenciesReceived: (userSettings, action) => {
      const { currencies } = action.payload;
      userSettings.currencies = currencies;
    },

    kpiSaved: (userSettings, action) => {
      userSettings.isAdding = false;
      userSettings.showKpiForm = true;
      userSettings.settings = action.payload;
      userSettings.settings.kpi = userSettings.settings.kpi.map((item, i) => {
        item.active = i === userSettings.settings.kpi.length - 1;
        return item;
      });
    },
    userKpiSelected: (userSettings, action) => {
      userSettings.settings.kpi = userSettings.settings.kpi.map((item) => {
        item.active = item.id === action.payload;
        return item;
      });
      userSettings.showKpiForm = true;
    },
    kpiRemoved: (userSettings, action) => {
      userSettings.settings = action.payload;
      userSettings.showKpiForm = false;
      userSettings.showDeleteKpiModal = false;
      userSettings.isAdding = false;
    },
    showKpiFormUpdated: (userSettings, action) => {
      userSettings.showKpiForm = action.payload;
    },
    showDeleteKpiModalUpdated: (userSettings, action) => {
      userSettings.showDeleteKpiModal = action.payload;
    },
    kpiUpdated: (userSettings, action) => {
      const { kpiIndex, kpi } = action.payload;
      userSettings.settings.kpi[kpiIndex] = kpi;
      userSettings.settings.kpi[kpiIndex].active = true;
    },
  },
});

export const {
  userSettingsRequested,
  userSettingsReceived,
  userSettingsRequestFailed,
  userSettingsSaveRequested,
  userSettingsSaveFailed,
  userSettingsSaved,
  codesRequested,
  codesReceived,
  currenciesRequested,
  currenciesReceived,
  kpiSaved,
  userKpiSelected,
  kpiRemoved,
  showKpiFormUpdated,
  showDeleteKpiModalUpdated,
  kpiUpdated,
} = userSettings.actions;
export default userSettings.reducer;

/**
 * Get settings
 */
export const getUserSettings = () =>
  apiCallBegan({
    url: '/api/settings/user',
    method: 'GET',
    onStart: userSettingsRequested.type,
    onSuccess: userSettingsReceived.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.userSettings_failedToGetUserSettings),
        },
      });
      return { type: userSettingsRequestFailed.type };
    },
  });

/**
 * Save settings
 *
 * @param data
 */
export const saveUserSettings = (data) =>
  apiCallBegan({
    url: '/api/settings/user',
    method: 'POST',
    data,
    onStart: userSettingsSaveRequested.type,
    onSuccess: (response) => ({
      type: userSettingsSaved.type,
      payload: response,
    }),
    onError: userSettingsSaveFailed.type,
  });

/**
 *  Get active operation codes for operation form
 */
export const getOperationCodes = () =>
  apiCallBegan({
    url: '/api/user-settings/active-codes',
    method: 'GET',
    onStart: codesRequested.type,
    onSuccess: codesReceived.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.userSettings_failedToGetOperationCodes),
        },
      });
    },
  });

/**
 *  Get currencies operation codes for operation form
 */
export const getCurrencies = () =>
  apiCallBegan({
    url: '/api/currencies',
    method: 'GET',
    onStart: currenciesRequested.type,
    onSuccess: currenciesReceived.type,
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.settings_failedToGetCurrencies),
        },
      });
    },
  });

/**
 * Adds an initial Key Performance Indicator (KPI) to the user settings.
 *
 * @param {string} name - The name of the KPI.
 * @param {object} settings - The user settings.
 * @returns {Object} The API call action object.
 */
export const addInitialUserKpi = (name, settings) => {
  const data = cloneDeep(settings);
  const { kpi: kpis } = data;
  const newName = nextName(
    kpis.map((kpi) => kpi.name),
    name,
  );

  const kpi = {
    ...initialKpi,
    name: newName,
    id: uuidv4(),
  };
  data.kpi = [...data.kpi, kpi];

  data.kpi = data.kpi.map(({ active, ...rest }) => rest);

  return apiCallBegan({
    url: '/api/settings/user',
    method: 'POST',
    data,
    onStart: userSettingsSaveRequested.type,
    onSuccess: (response) => ({
      type: kpiSaved.type,
      payload: response,
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.userSettings_failedToAddInitialUserKpi),
        },
      });
      return { type: userSettingsSaveFailed.type };
    },
  });
};

/**
 * Removes a KPI from user settings.
 *
 * @param {string} id - The ID of the KPI to be removed.
 * @param {Object} settings - The user settings object.
 * @returns {Object} The API call action object.
 */
export const removeUserKpi = (id, settings) => {
  const data = cloneDeep(settings);
  const index = data.kpi.findIndex((item) => item.id === id);
  data.kpi.splice(index, 1);

  return apiCallBegan({
    url: '/api/settings/user',
    method: 'POST',
    data,
    onStart: userSettingsSaveRequested.type,
    onSuccess: (response) => ({
      type: kpiRemoved.type,
      payload: response,
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.userSettings_failedToRemoveUserKpi),
        },
      });
      return { type: userSettingsSaveFailed.type };
    },
  });
};

/**
 * Updates the KPI with the given value and sends an API request to update the settings.
 *
 * @param {Object} kpi - The new KPI value to update.
 * @param {Object} settings - The current settings object to update.
 * @returns {Object} The API call action object.
 */
export const updateUserKpi = (kpi, settings) => {
  const data = cloneDeep(settings);
  const kpiIndex = data.kpi.findIndex((item) => item.active);

  if (data.kpi.some((k) => k.name.toUpperCase().trim() === kpi.name.toUpperCase().trim() && !k.active)) {
    toast({
      message: {
        type: 'Error',
        content: i18n.t(translations.settings_nameMustBeUnique, {
          type: i18n.t(translations.kpi),
          name: kpi.name,
        }),
      },
    });
    kpi.name = data.kpi[kpiIndex].name;
  }
  data.kpi[kpiIndex] = kpi;

  return apiCallBegan({
    url: '/api/settings/user',
    method: 'POST',
    data,
    onSuccess: () => ({
      type: kpiUpdated.type,
      payload: { kpiIndex, kpi },
    }),
    onError: () => {
      toast({
        message: {
          type: 'Error',
          content: i18n.t(translations.userSettings_failedToUpdateUserKpi),
        },
      });
    },
  });
};
